import type { ApiClient } from '@ghp/api-client'
import type { LoginRequest } from '@ghp/domain'
import { queryOptions, type QueryClient } from '@tanstack/react-query'

export type AuthApi = ApiClient['auth']

/**
 * La session vue du back-office — **un jeton d'accès, et rien d'autre**.
 *
 * La SPA ne détient pas le jeton de rafraîchissement : il voyage en cookie `httpOnly`, que
 * JavaScript ne voit jamais ([D3](../../../../../docs/ARCHITECTURE.md#d3)). Il n'y a donc rien
 * à ranger nulle part, et c'est le contraire exact du terrain, qui persiste sa session en
 * IndexedDB pour rouvrir sans réseau (D22). Ici, un onglet fermé est une session oubliée ; le
 * cookie, lui, laisse la reprendre sans mot de passe.
 *
 * ⚠️ Pas de `teamId` dans un jeton de bureau (M6) : le back-office voit tout et filtre par
 * équipe à l'écran.
 */
export type BackOfficeSession =
  { state: 'signed_in'; accessToken: string } | { state: 'signed_out' }

export const SIGNED_OUT: BackOfficeSession = { state: 'signed_out' }

/**
 * Le compte s'est authentifié, mais c'est un compte de terrain — le serveur a livré son
 * refresh **dans le corps** au lieu du cookie (D3), le seul canal que cette app sache tenir.
 *
 * Un type à part parce que ce n'est ni un refus d'identifiants ni une panne : laisser entrer
 * ouvrirait une session que le premier rechargement effacerait, sans rien à dire à
 * l'utilisateur. Le dire à la porte nomme la seule action utile — ouvrir l'app de contrôle.
 */
export class FieldAccountError extends Error {
  override readonly name = 'FieldAccountError'

  constructor() {
    super('Compte de terrain : sa session ne se tient pas depuis le back-office.')
  }
}

/**
 * Le serveur demande à quelle **équipe** ce compte ouvre sa session (M6) — le premier temps de la
 * connexion en deux temps de l'app de contrôle, que le back-office ne sait pas jouer.
 *
 * ⚠️ **Distinct de `FieldAccountError`, et c'est le correctif de #176.** Les deux cas partageaient
 * ce type, donc le même message : un agent devenu membre de deux équipes s'entendait répondre
 * « ce compte est un compte de terrain », phrase exacte mais qui **tait la seule chose nouvelle**
 * — il en était déjà un la veille, et il entrait. Le diagnostic juste du serveur ressortait en
 * fausse piste, et c'est ce qui a coûté le plus de temps à l'origine de cette issue.
 */
export class TeamSelectionError extends Error {
  override readonly name = 'TeamSelectionError'

  constructor() {
    super('Ce compte doit choisir son équipe, ce que le back-office ne propose pas.')
  }
}

/**
 * La session lue au démarrage : **le cookie porte tout**, donc un `/auth/refresh` sans corps
 * suffit à savoir si l'onglet est encore connecté.
 */
export function sessionQuery(api: AuthApi) {
  return queryOptions({
    queryKey: ['auth', 'session'],
    queryFn: () => readSession(api),
    // La fraîcheur ne vient pas du temps : `signIn` et `signOut` écrivent cette clé, et le
    // renouvellement du jeton avant ses 15 minutes (M23) est porté par `session-renewal.ts`
    // — qui écrit cette clé lui aussi, sans jamais déconnecter sur une simple coupure.
    staleTime: Infinity,
    retry: false,
  })
}

/** La lecture que font les deux gardes de route — celle de la coquille et celle de `/login`. */
export function ensureSession(queryClient: QueryClient, api: AuthApi): Promise<BackOfficeSession> {
  return queryClient.ensureQueryData(sessionQuery(api))
}

/**
 * ⚠️ Aucun échec ne remonte : refus du serveur, cookie absent ou serveur injoignable laissent
 * tous la même situation — cet onglet n'a pas de session. La cause n'a d'intérêt que lorsque
 * l'utilisateur agit, et elle se reformule alors d'elle-même à la tentative de connexion, où
 * elle est actionnable (`failureFor`, écran de connexion).
 */
async function readSession(api: AuthApi): Promise<BackOfficeSession> {
  try {
    const { accessToken } = await api.refresh()
    return { state: 'signed_in', accessToken }
  } catch {
    return SIGNED_OUT
  }
}

/**
 * La connexion — en un seul temps, contrairement au terrain : un compte de bureau n'a pas
 * d'équipe à choisir (M6), et les deux réponses qui le contrediraient disent la même chose.
 */
export async function openSession(
  api: AuthApi,
  credentials: LoginRequest,
): Promise<BackOfficeSession> {
  const response = await api.login(credentials)

  if (response.status === 'team_selection_required') throw new TeamSelectionError()
  if (response.refreshToken !== undefined) throw new FieldAccountError()

  return { state: 'signed_in', accessToken: response.accessToken }
}

/**
 * La déconnexion. Le corps est vide : le cookie porte le jeton à révoquer, la SPA ne l'a
 * jamais vu (D3).
 *
 * La révocation est un **meilleur effort**, comme côté terrain : sans serveur, elle échoue et
 * l'utilisateur quitte quand même son écran. ⚠️ Ce qu'on accepte alors : le cookie reste
 * valable jusqu'à son expiration (7 j, M23) — retenir quelqu'un devant un écran qu'il veut
 * quitter ne révoquerait rien de plus.
 */
export async function closeSession(api: AuthApi): Promise<void> {
  try {
    await api.logout()
  } catch {
    // Voir ci-dessus : rien à rattraper ici, la session locale part de toute façon.
  }
}
